import { PausableTimeout } from 'widgets/toolbox/PausableTimeout';

/**
 * @description Base CoreGlobalAlerts implementation.
 * @param GlobalAlerts Base GlobalAlerts for extending
 * @returns Core GlobalAlerts class
 */
function CoreGlobalAlertsClassCreator(GlobalAlerts: import('widgets/global/GlobalAlerts').TGlobalAlerts) {
    /**
     * @category widgets
     * @subcategory global
     * @class CoreGlobalAlerts
     * @augments GlobalAlerts
     * @classdesc Global Alerts with accessibility alert role possibility.
     * Acts as a popup with delayed disappearing. Popup will render user action description.
     * @property {boolean} data-widget - Widget name `globalAlerts`
     * @property {number} data-timeout - Value of hide timeout in milliseconds
     * @property {object} data-global-alerts - Global alerts localization messages
     * Possible values are: `error500`
     *
     * @example <caption>Global Alerts widget</caption>
     * <div
     *     class="b-global_alerts ${isAccessibilityAlertsHidden ? 'm-visually_hidden' : ''}"
     *     data-widget="globalAlerts"
     *     data-tau="global_alerts"
     *     data-global-alerts='{
     *         "error500": "${Resource.msg('alert.error500', 'global', null)}"
     *     }'
     *     aria-live="polite"
     * >
     *     <div
     *         hidden="hidden"
     *         data-ref="item"
     *         class="b-global_alerts-item"
     *         role="alert"
     *         data-event-mouseover="pauseAlert"
     *         data-event-mouseout="resumeAlert"
     *         data-tau="global_alerts_item"
     *     ></div>
     * </div>
     */
    class CoreGlobalAlerts extends GlobalAlerts {
        prefs() {
            return {
                successClassName: 'm-success',
                srOnlyClassName: 'b-sr_only',
                ...super.prefs()
            };
        }

        /**
         * @description Show Global Alert by starting pausable timeout
         * @param data - event data
         * @param data.accessibilityAlert - Message to be shown and pronounced
         * @param type - type of the message
         */
        showAlert(data: { accessibilityAlert?: string }, type?: string) {
            if (data?.accessibilityAlert) {
                if (this.shown) {
                    this.hideAlert();
                }

                if (type === 'error') {
                    this.ref('item').addClass(this.prefs().errorClassName);
                } else if (type === 'success') {
                    this.ref('item').addClass(this.prefs().successClassName);
                } else if (type === 'screen-reader') {
                    this.ref('item').addClass(this.prefs().successClassName);
                    this.ref('item').addClass(this.prefs().srOnlyClassName);
                }

                this.ref('item').setText(data.accessibilityAlert).show();
                this.shown = true;

                this.pausableTimeout = new PausableTimeout(() => this.hideAlert(), this.prefs().timeout);
            }
        }

        /**
         * @description Hide Global Alert and clear timeout
         */
        hideAlert() {
            this.ref('item').removeClass(this.prefs().errorClassName).removeClass(this.prefs().successClassName).setText('').hide();
            this.shown = false;
            this.disposePausableTimeout();
        }
    }

    return CoreGlobalAlerts;
}

export type TCoreGlobalAlerts = ReturnType<typeof CoreGlobalAlertsClassCreator>;

export type TCoreGlobalAlertsInstance = InstanceType<TCoreGlobalAlerts>;

export default CoreGlobalAlertsClassCreator;
